import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { SearchAddToAlbumTarget } from '../types/searchAddToAlbum';

type SearchOverlayContextValue = {
  isOpen: boolean;
  addToAlbumTarget: SearchAddToAlbumTarget | null;
  openSearch: () => void;
  openSearchForAlbum: (target: SearchAddToAlbumTarget) => void;
  clearAddToAlbumTarget: () => void;
  closeSearch: () => void;
};

const SearchOverlayContext = createContext<SearchOverlayContextValue | null>(null);

export function SearchOverlayProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [addToAlbumTarget, setAddToAlbumTarget] = useState<SearchAddToAlbumTarget | null>(null);

  const openSearch = useCallback(() => {
    setAddToAlbumTarget(null);
    setIsOpen(true);
  }, []);

  const openSearchForAlbum = useCallback((target: SearchAddToAlbumTarget) => {
    setAddToAlbumTarget(target);
    setIsOpen(true);
  }, []);

  const clearAddToAlbumTarget = useCallback(() => setAddToAlbumTarget(null), []);

  const closeSearch = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({
      isOpen,
      addToAlbumTarget,
      openSearch,
      openSearchForAlbum,
      clearAddToAlbumTarget,
      closeSearch,
    }),
    [isOpen, addToAlbumTarget, openSearch, openSearchForAlbum, clearAddToAlbumTarget, closeSearch],
  );

  return <SearchOverlayContext.Provider value={value}>{children}</SearchOverlayContext.Provider>;
}

export function useSearchOverlay() {
  const ctx = useContext(SearchOverlayContext);
  if (!ctx) {
    throw new Error('useSearchOverlay must be used within SearchOverlayProvider');
  }
  return ctx;
}
